import type { CatalogDetails, CatalogItem, CatalogRef, CatalogType, ReleaseDetails, ReleasesResult, ReleaseTracks, SubtitleSearch, SubtitleTrack } from './protocol'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message)
  }
}

// In the Discord Activity the session also travels in a header: the
// partitioned cookie may be refused there.
let sessionToken: string | null = null

export function setSessionToken(token: string | null) {
  sessionToken = token
}

export function getSessionToken() {
  return sessionToken
}

async function request<T>(
  method: string,
  url: string,
  body?: unknown,
  headers: Record<string, string> = {},
  signal?: AbortSignal,
): Promise<T> {
  if (sessionToken && !headers.Authorization) headers = { ...headers, Authorization: `Session ${sessionToken}` }
  const init: RequestInit = { method, headers: { ...headers }, credentials: 'same-origin', signal }
  if (body instanceof FormData) {
    init.body = body
  } else if (body !== undefined) {
    init.body = JSON.stringify(body)
    ;(init.headers as Record<string, string>)['Content-Type'] = 'application/json'
  }
  const res = await fetch(url, init)
  if (!res.ok) {
    let message = res.statusText
    let code: string | undefined
    try {
      const body = await res.json()
      message = body.error ?? message
      code = body.code
    } catch {
      // keep status text
    }
    throw new ApiError(res.status, message, code)
  }
  if (res.status === 204 || res.status === 202) return undefined as T
  return res.json() as Promise<T>
}

export interface Session {
  userId: string
  nickname: string
}

/** A catalog entry, the season of a series included. */
export interface PickedTitle {
  type: CatalogType
  id: number
  season?: number
}

/** A release picked in the catalog, with what it was picked for. */
export interface PickedRelease {
  release: { provider: string; id: string }
  catalog: PickedTitle
}

export interface Features {
  catalog: boolean
  providers: string[]
  /** Subtitles can be searched on OpenSubtitles. */
  subtitles?: boolean
  /** How to reach the owner, for the terms and privacy pages. */
  contact?: string
  /** Present when the server runs the Discord Activity. */
  discord?: { clientId: string; siteUrl: string }
}

export interface DiscordSessionRequest {
  accessToken: string
  instanceId: string
  guildId: string | null
  channelId: string | null
  channelName: string
  /** Activates the Discord server for new rooms, once. */
  createToken?: string
}

/** A Discord user signed into the room of their activity instance. */
export interface DiscordSession {
  sessionToken: string
  roomId: string
  user: { id: string; nickname: string; avatar?: string }
}

/** A Discord user taken to the website (see api.handoff). */
export interface LinkedSession {
  sessionToken: string
  userId: string
  nickname: string
  avatar?: string
}

/**
 * Catalog requests are allowed to room owners (by session) or with the room
 * creation token, used on the home page.
 */
function catalogAuth(token?: string): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export const api = {
  session: () => request<Session>('POST', '/api/session'),
  setNickname: (nickname: string) => request<Session>('PUT', '/api/me', { nickname }),
  /** Creates a room with a release, or with only a title (catalog) chosen. */
  createRoom: (token: string, name: string, picked?: PickedRelease | { catalog: PickedTitle }) =>
    request<{ id: string; name: string }>('POST', '/api/rooms', { name, ...picked }, { Authorization: `Bearer ${token}` }),
  setTitle: (roomId: string, title: PickedTitle) => request<CatalogRef>('PUT', `/api/rooms/${roomId}/title`, title),
  clearTitle: (roomId: string) => request<void>('DELETE', `/api/rooms/${roomId}/title`),
  searchSubtitles: (roomId: string, q: { languages: string[]; season?: number; episode?: number }) => {
    const p = new URLSearchParams({ languages: q.languages.join(',') })
    if (q.season) p.set('season', String(q.season))
    if (q.episode) p.set('episode', String(q.episode))
    return request<SubtitleSearch>('GET', `/api/rooms/${roomId}/subtitles/search?${p}`)
  },
  addSubtitle: (roomId: string, s: { fileId: number; language: string; release: string }) =>
    request<{ track: SubtitleTrack }>('POST', `/api/rooms/${roomId}/subtitles`, s),
  room: (id: string) => request<{ id: string; name: string }>('GET', `/api/rooms/${id}`),
  deleteRoom: (id: string) => request<void>('DELETE', `/api/rooms/${id}`),
  addMagnet: (id: string, magnet: string) => request<void>('POST', `/api/rooms/${id}/source`, { magnet }),
  addTorrentFile: (id: string, file: File) => {
    const form = new FormData()
    form.append('torrent', file)
    return request<void>('POST', `/api/rooms/${id}/source`, form)
  },
  addRelease: (id: string, picked: PickedRelease) => request<void>('POST', `/api/rooms/${id}/source`, picked),

  features: () => request<Features>('GET', '/api/features'),
  discordToken: (code: string) => request<{ accessToken: string }>('POST', '/api/discord/token', { code }),
  discordSession: (body: DiscordSessionRequest) =>
    request<DiscordSession>('POST', '/api/discord/session', body),
  /** A one-time code that signs the current user in on the website. */
  handoff: () => request<{ code: string }>('POST', '/api/handoff'),
  redeemHandoff: (code: string) => request<LinkedSession>('POST', '/api/handoff/redeem', { code }),
  catalogSearch: (q: string, lang: string, token?: string, signal?: AbortSignal) =>
    request<{ items: CatalogItem[] }>('GET', `/api/catalog/search?q=${encodeURIComponent(q)}&lang=${lang}`, undefined, catalogAuth(token), signal),
  catalogDetails: (type: CatalogType, id: number, lang: string, token?: string) =>
    request<CatalogDetails>('GET', `/api/catalog/${type}/${id}?lang=${lang}`, undefined, catalogAuth(token)),
  catalogReleases: (type: CatalogType, id: number, season: number, token?: string, signal?: AbortSignal) =>
    request<ReleasesResult>('GET', `/api/catalog/${type}/${id}/releases?season=${season}`, undefined, catalogAuth(token), signal),
  releaseDetails: (provider: string, id: string, token?: string) =>
    request<ReleaseDetails>('GET', `${releasePath(provider, id)}/details`, undefined, catalogAuth(token)),
  releaseTracks: (provider: string, id: string, season: number, token?: string) =>
    request<ReleaseTracks>('GET', `${releasePath(provider, id)}/tracks?season=${season}`, undefined, catalogAuth(token)),
}

/** URL of a TMDb image through the server's cache. */
export function imageUrl(size: 'w92' | 'w185' | 'w342' | 'w500' | 'w780' | 'w1280', path?: string) {
  return path ? `/api/images/${size}${path}` : undefined
}

// Small localStorage helpers that never throw (private mode, blocked storage).
export const storage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  },
  set(key: string, value: string) {
    try {
      localStorage.setItem(key, value)
    } catch {
      // ignore
    }
  },
  remove(key: string) {
    try {
      localStorage.removeItem(key)
    } catch {
      // ignore
    }
  },
}

export const NICK_KEY = 'chillio.nick'
export const TOKEN_KEY = 'chillio.createToken'

const releasePath = (provider: string, id: string) => `/api/catalog/releases/${encodeURIComponent(provider)}/${encodeURIComponent(id)}`
